// Faceted-search parsing for the ⌘K command palette.
//
// Users can narrow document results by typing facet tokens inline in the
// search box. Supported grammar (tokens are space-separated, case-insensitive):
//
//   status:<value>   — matches doc.status exactly (review|draft|published|superseded|…)
//   type:<value>     — matches doc.docType by substring/prefix
//   review:overdue   — families whose latest nextReview is before today
//   overdue          — shorthand for review:overdue
//
// Anything that is not a recognized facet token is treated as free text and
// matches title/family as before. Multiple facets AND together.

export interface ParsedQuery {
  facets: PaletteFacets;
  freeText: string;
}

export interface PaletteFacets {
  status?: string;
  type?: string;
  overdue?: boolean;
}

/** Minimal shape of a doc the predicate needs (subset of GitDocEntry). */
export interface FacetTarget {
  status: string;
  docType: string;
  nextReview?: string;
}

/**
 * Parse a raw query string into structured facets plus the remaining free text.
 * Pure and side-effect free.
 */
export function parseQuery(raw: string): ParsedQuery {
  const facets: PaletteFacets = {};
  const freeTokens: string[] = [];

  for (const token of raw.trim().split(/\s+/)) {
    if (!token) continue;
    const lower = token.toLowerCase();

    if (lower === "overdue" || lower === "review:overdue") {
      facets.overdue = true;
      continue;
    }

    const colon = token.indexOf(":");
    if (colon > 0) {
      const key = token.slice(0, colon).toLowerCase();
      const value = token.slice(colon + 1).toLowerCase();
      if (key === "status" && value) {
        facets.status = value;
        continue;
      }
      if (key === "type" && value) {
        facets.type = value;
        continue;
      }
      if (key === "review" && value === "overdue") {
        facets.overdue = true;
        continue;
      }
    }

    freeTokens.push(token);
  }

  return { facets, freeText: freeTokens.join(" ") };
}

/** True if any facet is active. */
export function hasFacets(facets: PaletteFacets): boolean {
  return Boolean(facets.status || facets.type || facets.overdue);
}

/**
 * Test a doc against parsed facets. `now` defaults to the current date and is
 * injectable for deterministic tests. Multiple facets AND together.
 */
export function matchesFacets(
  doc: FacetTarget,
  facets: PaletteFacets,
  now: Date = new Date(),
): boolean {
  if (facets.status && doc.status.toLowerCase() !== facets.status) {
    return false;
  }
  if (facets.type && !doc.docType.toLowerCase().includes(facets.type)) {
    return false;
  }
  if (facets.overdue) {
    if (!doc.nextReview) return false;
    const review = new Date(doc.nextReview);
    if (Number.isNaN(review.getTime()) || review.getTime() >= now.getTime()) {
      return false;
    }
  }
  return true;
}

/** Render a compact mono hint describing the active facets, e.g.
 * `status:review · type:methodology · overdue`. Empty string when none. */
export function describeFacets(facets: PaletteFacets): string {
  const parts: string[] = [];
  if (facets.status) parts.push(`status:${facets.status}`);
  if (facets.type) parts.push(`type:${facets.type}`);
  if (facets.overdue) parts.push("overdue");
  return parts.join(" · ");
}
